import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { when } from 'mobx';
import { observer } from 'mobx-react';
import { useFeature } from '@ole-ui/ole-ui-components';

import { Button } from '@patternfly/react-core';
import { Col, Row, Tabs, Tab } from 'react-bootstrap';
import { withErrorBoundary } from 'react-error-boundary';
import { Helmet } from 'react-helmet';

import { EnablementFeatureFlags } from 'config/featureFlags';
import { storesContext } from 'stores';
import { handleError } from 'services/ErrorService';
import GenericViewError from 'components/Error/GenericViewError';
import LearningPathList from 'components/LearningPath/List';
import CatalogCardList from 'components/Catalog/List';
import ResultList from 'components/Search/ResultList';
import './styles.scss';

const SearchV1 = () => {
  const {
    catalogStore,
    learningPathStore,
    routerStore,
    searchStore,
    userStore,
  } = useContext(storesContext);
  const { t } = useTranslation();
  const { route } = routerStore;
  const { q: query, courses } = route.params;
  const isSkillsPathsEnabled = useFeature({
    section: 'enablements',
    flagName: EnablementFeatureFlags.SkillsPathsEnabled,
    defaultFlagValue: false,
  });

  useEffect(() => {
    searchStore.query = query;
    if (courses) {
      searchStore.courses = courses;
    }
    try {
      searchStore.search(query, courses);
    } catch (error) {
      console.error(error);
    }
    when(
      () => catalogStore.entries.length && !userStore.isAlaCarte,
      () => {
        catalogStore.filterEntries();
      },
    );
    when(
      () => learningPathStore.entries.length && !userStore.isAlaCarte,
      () => {
        learningPathStore.filterEntries();
      },
    );
    // when component is about to be destroyed, clear the search && reset
    // catalog & learning path results
    // Update preset filters when route changes
  }, [query, route]);

  useEffect(() => {
    return () => {
      searchStore.query = '';
    };
  }, [route.name]);

  const searchSuggestion = (suggestedQuery) => {
    routerStore.navigate('search', {
      q: suggestedQuery,
      searchWithin: 'all',
    });
  };

  const suggestionText = searchStore.suggestion
    .map((term) => term[0])
    .join(' ');

  const suggestionHTML = searchStore.suggestion.map((term) =>
    term[1] ? <em>{term[0]} </em> : `${term[0]} `,
  );

  return (
    <div className="container-grid-responsive">
      <Helmet title={`${t('Search')} - Red Hat Learning`} />
      <Row>
        <Col xs={12}>
          <Tabs defaultActiveKey={1} id="search-results-tabs">
            <Tab eventKey={1} title={t('Results within text').toUpperCase()}>
              {searchStore.finishedSearch &&
                searchStore.suggestion.some((entry) => entry[1]) && (
                  <div className="suggestion-container">
                    {t('Did you mean: ')}
                    <Button
                      variant="link"
                      onClick={() => searchSuggestion(suggestionText)}
                      size="lg"
                    >
                      {suggestionHTML}
                    </Button>
                  </div>
                )}
              <section className="all-search-results-container">
                <ResultList />
              </section>
            </Tab>
            {!userStore.isAlaCarte && !userStore.isExternalSubscriber && (
              <Tab eventKey={2} title={t('Catalog').toUpperCase()}>
                <section className="catalog-search-results-container">
                  <CatalogCardList />
                </section>
              </Tab>
            )}
            {isSkillsPathsEnabled && (
              <Tab eventKey={3} title={t('Skills Paths').toUpperCase()}>
                <section className="learning-paths-search-results-container">
                  <LearningPathList />
                </section>
              </Tab>
            )}
          </Tabs>
        </Col>
      </Row>
    </div>
  );
};

export default withErrorBoundary(
  observer(SearchV1),
  GenericViewError,
  handleError,
);
